import React, { useState, useRef, useEffect } from "react"
import { getTranslations } from "./components/translations"

// Helper function to detect if a hex color is light (needs dark border)
const isLightColor = (hexColor) => {
  if (!hexColor || !hexColor.startsWith("#")) return false

  const hex = hexColor.replace("#", "")
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex

  const r = parseInt(fullHex.substring(0, 2), 16)
  const g = parseInt(fullHex.substring(2, 4), 16)
  const b = parseInt(fullHex.substring(4, 6), 16)

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.85
}

function MaterialTabs({
  materials,
  selectedMaterials,
  onMaterialChange,
  language = "it",
  openAccordion,
  onAccordionOpen,
}) {
  const [activeTab, setActiveTab] = useState(
    materials[0]?.technicalName || null,
  )
  const [isOpenInternal, setIsOpenInternal] = useState(false)

  // Sync with external control (mobile: only 1 open at a time)
  useEffect(() => {
    if (openAccordion !== undefined) {
      setIsOpenInternal(openAccordion === "finiture")
    }
  }, [openAccordion])

  const isOpen = isOpenInternal
  const setIsOpen = (val) => {
    setIsOpenInternal(val)
    if (val && onAccordionOpen) onAccordionOpen("finiture")
  }
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef(null)
  const translations = getTranslations(language)

  // Update content height when isOpen changes
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    } else {
      setContentHeight(0)
    }
  }, [isOpen, activeTab])

  // Ensure first tab is selected when materials change or accordion opens
  useEffect(() => {
    if (materials && materials.length > 0) {
      const firstMaterialName = materials[0]?.technicalName
      // Only update if activeTab is not set or doesn't exist in current materials
      const activeExists = materials.some((m) => m.technicalName === activeTab)
      if (!activeTab || !activeExists) {
        setActiveTab(firstMaterialName)
      }
    }
  }, [materials, activeTab])

  // Get translated title for material type
  const getTranslatedTitle = (technicalName, mat) => {
    // Try technical type translations first
    if (mat?.technicalTypeTranslations?.[technicalName]?.[language]) {
      return mat.technicalTypeTranslations[technicalName][language]
    }

    // Fallback to global translations
    if (translations.materialTypes?.[technicalName.toLowerCase()]) {
      return translations.materialTypes[technicalName.toLowerCase()]
    }

    // Fallback to technical name
    return technicalName
  }

  // Get image URL helper
  const getImageUrl = (thumbnail) => {
    if (thumbnail && thumbnail.startsWith("#")) return thumbnail
    if (
      thumbnail &&
      (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    )
      return thumbnail
    if (thumbnail) {
      const cleanPath = thumbnail.replace(/^\.\//, "")
      return `https://moroso-ayea.ensoul.work/${cleanPath}`
    }
    return ""
  }

  if (!materials || materials.length === 0) return null

  const activeMaterial = materials.find((m) => m.technicalName === activeTab)

  return (
    <div className="ay-w-full ay-border-b ay-border-gray-300">
      {/* FINITURE label + small swatches + chevron - CLICKABLE ACCORDION */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ay-w-full ay-py-4 ay-px-0 ay-flex ay-items-end ay-transition-colors"
      >
        <div className="ay-flex ay-w-1/3 md:ay-w-1/5 ay-items-end ay-pb-0.5">
          <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider ay-text-left">
            FINITURE
          </span>
        </div>
        <div className="ay-flex ay-w-2/3 md:ay-w-4/5 ay-items-end ay-justify-between">
          <div className="ay-flex ay-gap-2">
            {materials.slice(0, 3).map((mat) => {
              const selectedMat = selectedMaterials[mat.technicalName]
              const needsDarkBorder =
                selectedMat?.thumbnail && isLightColor(selectedMat.thumbnail)
              return (
                <div
                  key={mat.technicalName}
                  onClick={(e) => {
                    e.stopPropagation()
                    setActiveTab(mat.technicalName)
                    setIsOpen(true)
                  }}
                  className="ay-w-8 ay-h-8 ay-overflow-hidden ay-cursor-pointer hover:ay-opacity-80 ay-transition-opacity"
                  style={{
                    border: needsDarkBorder ? "1px solid #000" : "none",
                  }}
                >
                  {selectedMat?.thumbnail ? (
                    selectedMat.thumbnail.startsWith("#") ? (
                      <div
                        className="ay-w-full ay-h-full"
                        style={{ backgroundColor: selectedMat.thumbnail }}
                      />
                    ) : (
                      <img
                        src={getImageUrl(selectedMat.thumbnail)}
                        alt=""
                        className="ay-w-full ay-h-full ay-object-cover ay-rotate-90"
                      />
                    )
                  ) : (
                    <div className="ay-w-full ay-h-full ay-bg-gray-200" />
                  )}
                </div>
              )
            })}
          </div>
          <svg
            className={`ay-w-4 ay-h-4 ay-text-gray-400 ay-transition-transform ay-duration-300 ${
              isOpen ? "ay-rotate-180" : "ay-rotate-0"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Accordion Content - Tabs + Material Grid */}
      <div
        className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
        style={{
          maxHeight: `${contentHeight}px`,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div className="ay-flex">
          <div className="ay-w-1/3 md:ay-w-1/5"></div>
          <div ref={contentRef} className="ay-w-2/3 md:ay-w-4/5">
            {/* Horizontal Tabs */}
            <div className="ay-flex ay-gap-3">
              {materials.map((mat) => {
                const isActive = mat.technicalName === activeTab
                return (
                  <button
                    key={mat.technicalName}
                    onClick={() => setActiveTab(mat.technicalName)}
                    className={`ay-px-3 ay-py-1 ay-text-[9px] ay-font-serif ay-font-bold ay-uppercase ay-whitespace-nowrap ay-rounded-full ay-transition-[background-color,color] ay-duration-200 ${
                      isActive
                        ? "ay-bg-black ay-text-white"
                        : "ay-bg-transparent ay-text-gray-700 hover:ay-text-gray-500"
                    }`}
                  >
                    {getTranslatedTitle(mat.technicalName, mat)}
                  </button>
                )
              })}
            </div>

            {/* Tab Content - Material Grid - 3 COLUMNS ONLY */}
            {activeMaterial && (
              <div className="ay-py-4">
                <div className="ay-grid ay-grid-cols-3 ay-gap-1">
                  {activeMaterial.variations.map((material) => {
                    const isSelected =
                      selectedMaterials[activeTab]?.slug === material.slug ||
                      selectedMaterials[activeTab]?.name === material.name
                    const needsDarkBorder =
                      material.thumbnail && isLightColor(material.thumbnail)

                    return (
                      <button
                        key={material.id || material.slug || material.name}
                        onClick={() => onMaterialChange(activeTab, material)}
                        className="ay-group/mat ay-cursor-pointer"
                      >
                        {/* Container - fixed aspect ratio */}
                        <div className="ay-w-full ay-aspect-square ay-relative ay-flex ay-items-center ay-justify-center">
                          {/* Tile */}
                          <div
                            className={`ay-absolute ay-inset-[0%] ay-overflow-hidden ay-transition-[inset] ay-duration-300 ay-ease-out ${
                              isSelected
                                ? "!ay-inset-[10%]"
                                : "group-hover/mat:ay-inset-[10%]"
                            }`}
                            style={{
                              border: needsDarkBorder
                                ? "1px solid #000"
                                : "none",
                            }}
                          >
                            {material.thumbnail &&
                            material.thumbnail.startsWith("#") ? (
                              <div
                                className="ay-w-full ay-h-full"
                                style={{ backgroundColor: material.thumbnail }}
                              />
                            ) : (
                              <img
                                src={getImageUrl(material.thumbnail)}
                                alt={material.name}
                                className="ay-w-full ay-h-full ay-object-cover ay-rotate-90"
                                onError={(e) => {
                                  e.target.onerror = null
                                  e.target.src =
                                    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23cccccc"/></svg>'
                                }}
                              />
                            )}
                          </div>
                          {/* Material name - bottom of container */}
                          <div
                            className={`ay-absolute ay-bottom-0 ay-left-0 ay-right-0 ay-text-center ay-transition-opacity ay-duration-200 ${
                              isSelected
                                ? "ay-opacity-100"
                                : "ay-opacity-0 group-hover/mat:ay-opacity-100"
                            }`}
                          >
                            <span className="ay-text-black ay-font-serif ay-font-bold ay-text-[9px] ay-leading-none ay-truncate ay-block">
                              {material.displayName || material.name}
                            </span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MaterialTabs
